//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useCallback, useEffect, useState } from "react";

// === API ===
import {
  getFocusedField,
  getFocusedImage,
  on,
  onDirectionIndicator,
  onImage,
  removeEntry,
  removeListener,
  resetImage,
  returnFieldFocus,
  sendMessageData,
  updateFieldCoords,
} from 'api/prokey/prokey.websocket';
import { useImageElement } from './useImageElement';
import { canvasToImage, clientToCanvas, displayRotation, drawRotatedImage, entryImageRect, imageToCanvas, imageToRes, pixelBounds, rectCorners, rotatedDimensions, selectionRect } from './imageGeometry';

//#endregion

//-----------------------------------------------------------------
export function ImageCanvas(props) {
  //-----------------------------------------------------------------

  //#region [VALUES] Props

  const {
    settings,
    container_ref,
  } = props;

  const { is_full_size, is_magnifier_held } = settings || {};

  //#endregion

  //#region [VALUES] State

  // the canvas' context is stored once it's created
  const [ image_data , setImageData ] = useState( getFocusedImage() );
  const image = useImageElement( image_data, resetImage );
  const rotation = displayRotation(image_data?.info);
  const [ field      , setField     ] = useState( getFocusedField() );
  const [ block      , setBlock     ] = useState(  {}  );

  const [ ref_base        , setRefBase        ] = useState( null )
  const [ ref_magnify     , setRefMagnify     ] = useState( null )
  const [ context_base    , setContextBase    ] = useState( null );
  const [ context_magnify , setContextMagnify ] = useState( null );

  const [ image_width   , setImageWidth   ] = useState( 1 );
  const [ image_height  , setImageHeight  ] = useState( 1 );
  const [ canvas_width  , setCanvasWidth  ] = useState( 1 );
  const [ canvas_height , setCanvasHeight ] = useState( 1 );  
  const [ width_conversion  , setConversionWidth  ] = useState( 1 );
  const [ height_conversion , setConversionHeight ] = useState( 1 );

  const [ selected_item , setSelectedItem ] = useState( null );

  const [ canvas_highlights , setCanvasHighlights ] = useState( {} );

  const [ arrowDirection, setArrowDirection ] = useState(null);

  const setBaseRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d");
      setContextBase( context );
      setRefBase( node );
    }
  }, []);

  const setMagnifyRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d")
      setContextMagnify( context );
      setRefMagnify( node );
    }
  }, []);

  let showMagnifier = false;
  let drawHighlight = false;

  let startCoord = null;
  let endCoord = null;

  const buffer_x = 10;
  const buffer_y = 10;

  let mouse_x = 0;
  let mouse_y = 0;

  let magnifier_rect = null;
  let magnifier_mouse_follow = false;

  const getHighlightRect = useCallback(entry =>
    entryImageRect(entry, image_data?.info, width_conversion, height_conversion),
  [image_data?.info, width_conversion, height_conversion]);

  //#endregion

  //#region [STYLES]

  const _styles = {
    canvas_main: {
      position  : 'absolute',
      top       : is_full_size ? '' : 0,
      left      : is_full_size ? '' : 0,
      width     : is_full_size ? '' : '',
      height    : is_full_size ? '' : '',
      maxWidth  : is_full_size ? '100%' : '100%',
      maxHeight : is_full_size ? ''     : '100%',
      zIndex    : 20,
      overflow  : is_full_size ? 'none' : 'none',
      cursor    : 'crosshair',
    },
    canvas_misc: {
      position  : 'absolute',
      top       : is_full_size ? '' : 0,
      left      : is_full_size ? '' : 0,
      width     : is_full_size ? '' : '',
      height    : is_full_size ? '' : '',
      maxWidth  : is_full_size ? '100%' : '100%',
      maxHeight : is_full_size ? ''     : '100%',
      zIndex    : 20,
      overflow  : is_full_size ? 'none' : 'none',
      cursor    : 'crosshair',
    },
  }

  //#endregion

  //#region [EFFECT]
      
  useEffect( () => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData( 'TASK_VIEW_IMAGES_UNSUBSCRIBE' );
    }

    sendMessageData( 'TASK_VIEW_IMAGES_SUBSCRIBING' );

    window.addEventListener('beforeunload', unsubscribe );
    return () => {
      window.removeEventListener('beforeunload', unsubscribe );
    }
  }, [] );
  
  useEffect( () => {
    function _handleReconnect(data) {
      sendMessageData( 'TASK_VIEW_IMAGES_SUBSCRIBING', data );
    }

    on( 'connection', _handleReconnect );

    return () => {
      removeListener( 'connection', _handleReconnect );
    }
  }, [] );

  // ===

  useEffect(() => {
    if( !image ) return;

    setImageWidth ( image.naturalWidth  );
    setImageHeight( image.naturalHeight );

  }, [image, canvas_width, canvas_height]);

  // ===

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      drawMagnifier();
      setImageData(data)
    }

    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      setField(data);
      setArrowDirection(null);
    }

    //-----------------------------------------------------------------
    function handleBlockFocus(data) {
      //-----------------------------------------------------------------
      // if( block.id === data.id) return;
      setBlock(data);
    }

    //-----------------------------------------------------------------
    function handleImageClear(data) {
      //-----------------------------------------------------------------
      setField    (null);
      setBlock    ( {} );
      setImageData(null);
    }
      
    on( 'IMAGE'        , handleImage      );
    on( 'FIELD-FOCUS'  , handleFieldFocus );
    on( 'BLOCK-FOCUS'  , handleBlockFocus );
    on( 'IMAGES_CLEAR' , handleImageClear );
    
    return () => {
      removeListener( 'IMAGE'        , handleImage      );
      removeListener( 'FIELD-FOCUS'  , handleFieldFocus );
      removeListener( 'BLOCK-FOCUS'  , handleBlockFocus );
      removeListener( 'IMAGES_CLEAR' , handleImageClear );
    }
  }, [] );

  useEffect( () => {
    if( !image_data ) return;

    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      setImageData(data)
    }

    onImage( image_data.id, handleImage );
    
    return () => {
      removeListener( 'IMAGE-' + image_data.id , handleImage );
    }
  }, [image_data] )

  useEffect( () => {
    if( !image_data?.id ) return;

    //-----------------------------------------------------------------
    function handleDirection(data) {
      //-----------------------------------------------------------------
      setArrowDirection(data.direction)
    }

    onDirectionIndicator( handleDirection );

    return () => {
      removeListener( 'DIRECTION-INDICATOR' , handleDirection );
    }

  }, [image_data?.id] )

  useEffect( () => {
    if( !block.id ) return;
  
    //-----------------------------------------------------------------
    function createFieldHighlights( fields ) {
      //-----------------------------------------------------------------
      // return;
      if( !field || !image) return;
  
      const canvas_buffers = {};

      // Current Field
      const current_field_buffer = { id: field.Id, entries: new Map(), is_current: true }
  
      canvas_buffers[ field.Id ] = current_field_buffer;

      for( let index = 0; index < field.Entries.length; index++ ) {
        const entry = field.Entries[index];
        
        if( entry.PageNr != image_data.page_number ) {
          continue;
        }

        const is_selected = index === selected_item;
        let coords = getHighlightRect(entry);

        const canvas = document.createElement('canvas')
        canvas.width  = coords.width  + 4 ;
        canvas.height = coords.height + 4 ;
        const buffer = { canvas, x: coords.x, y: coords.y, is_selected };
        
        createFieldHighlight( canvas, coords, true, is_selected );
        current_field_buffer.entries.set( coords, buffer );
      }
  
      for( const block_field of fields ) {
        const is_current = block_field.Id === field.Id;
        if( is_current ) continue;
  
        const field_buffer = { id: block_field.Id, entries: new Map(), is_current }
  
        canvas_buffers[ block_field.Id ] = field_buffer;
  
        for( let index = 0; index < block_field.Entries.length; index++ ) {
          const entry = block_field.Entries[index];
          if( block_field.image_id !== image_data.id ) continue;
  
          const is_selected = index === selected_item;
  
          let coords = getHighlightRect(entry);
  
          const canvas = document.createElement('canvas')
          canvas.width  = coords.width  + 4 ;
          canvas.height = coords.height + 4 ;
          const buffer = { canvas, x: coords.x, y: coords.y, is_selected };
          
          createFieldHighlight( canvas, coords, is_current, is_selected );
          field_buffer.entries.set( coords, buffer );
        }
      }

      setCanvasHighlights( canvas_buffers );
  
      // if(drawHighlight && endCoord !== null) {
      //   context_base.fillStyle = 'rgb(255,255,0)';
      //   context_base.fillRect( startCoord.x, startCoord.y, endCoord.x-startCoord.x, endCoord.y-startCoord.y );
      // }
  
      // context_base.globalCompositeOperation = "source-over";
    }
  
    //-----------------------------------------------------------------
    function createFieldHighlight( canvas, { x, y, width, height }, is_curr_field, is_selected) {
      //-----------------------------------------------------------------
      const buffer_context = canvas.getContext('2d');
      buffer_context.drawImage( image, x, y, width, height, 0, 0, width, height );
  
      const color = is_curr_field ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'
  
      buffer_context.globalCompositeOperation = "darken";
  
      buffer_context.beginPath();
  
      if( is_curr_field && is_selected ) {
        buffer_context.lineWidth = 3;
        buffer_context.strokeStyle = "black";
        buffer_context.strokeRect( 2, 2, width, height);
      }
  
      buffer_context.fillStyle = color;
      buffer_context.fillRect(0, 0, width, height);
  
      buffer_context.closePath();
    }

    createFieldHighlights( block.fields );
  }, [field, block, image, image_data, selected_item, getHighlightRect] );

  useEffect( () => {
    if (context_base && image) {
      drawRotatedImage(context_base, image, rotation, canvas_highlights);
    }
  }, [image, rotation, context_base, canvas_highlights, image_width, image_height, canvas_width, canvas_height] );

  useEffect( () => {
    if( !is_full_size || !field || !image || !image_data || !ref_base ) return;

    const entry = field.Entries?.find( item => {
      const is_current_page = Number(item.PageNr) === Number(image_data.page_number);
      const has_coordinates = Number(item.Bottom) > Number(item.Top)
        && Number(item.Right) > Number(item.Left);
      return is_current_page && has_coordinates;
    });
    if( !entry ) return;

    const animation_frame = window.requestAnimationFrame( () => {
      const container = container_ref?.current;
      if( !container ) return;

      const highlight = getHighlightRect(entry);
      const canvas_rect = ref_base.getBoundingClientRect();
      const container_rect = container.getBoundingClientRect();
      const scale_x = canvas_rect.width  / ref_base.width;
      const scale_y = canvas_rect.height / ref_base.height;

      const center = imageToCanvas({ x: highlight.x + highlight.width / 2,
        y: highlight.y + highlight.height / 2 }, image.naturalWidth, image.naturalHeight, rotation);
      const field_center_x = canvas_rect.left - container_rect.left + container.scrollLeft
        + (center.x * scale_x);
      const field_center_y = canvas_rect.top - container_rect.top + container.scrollTop
        + (center.y * scale_y);

      container.scrollTo({
        left: Math.max( 0, field_center_x - (container.clientWidth  * 0.5) ),
        top : Math.max( 0, field_center_y - (container.clientHeight * 0.5) ),
        behavior: 'auto',
      });
    });

    return () => window.cancelAnimationFrame(animation_frame);
  }, [field, image, image_data, rotation, is_full_size, ref_base, container_ref, getHighlightRect] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function drawBaseCanvas() {
    if (context_base && image) {
      drawRotatedImage(context_base, image, rotation, canvas_highlights);
    }
  }

  function pointerPosition() {
    return clientToCanvas({ x: mouse_x, y: mouse_y }, ref_base);
  }

  function currentSelection() {
    return selectionRect(startCoord, endCoord,
      showMagnifier && !magnifier_mouse_follow ? magnifier_rect : null);
  }

  function sourcePoint(point) {
    return canvasToImage(point, image_width, image_height, rotation);
  }

  //-----------------------------------------------------------------
  function highlightCoordsStart() {
    //-----------------------------------------------------------------
    if(drawHighlight) return;

    drawHighlight = true;
    endCoord = null;
    startCoord = pointerPosition();
  }

  function highlightCoordsEnd() {
    if (!drawHighlight) return;
    endCoord = pointerPosition();
  }

  //-----------------------------------------------------------------
  function saveHighlightCoords() {
    //-----------------------------------------------------------------
    if (!drawHighlight || !startCoord || !endCoord) return;
    drawHighlight = false;
    const clamp = (value, limit) => Math.max(0, Math.min(limit, value));
    const rect = pixelBounds(rectCorners(currentSelection()).map(point => {
      const source = sourcePoint(point);
      return imageToRes({ x: clamp(source.x, image_width) / width_conversion,
        y: clamp(source.y, image_height) / height_conversion }, image_data?.info);
    }));
    const topLeft = { x: rect.x, y: rect.y };
    const bottomRight = { x: rect.x + rect.width, y: rect.y + rect.height };
    startCoord = null;
    endCoord = null;
    drawBaseCanvas();

    const width  = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    // prevent highlight on accidental right-click
    if( height < 15 || width < 15 ) return;

    updateFieldCoords({ field: {
      id: field?.Id,
      imageId: image_data.id,
      content: field?.Entries?.[0]?.Content,
      left: topLeft.x,
      top: topLeft.y,
      right: bottomRight.x,
      bottom: bottomRight.y,
    }});

    startCoord = null;
    endCoord = null;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    // Left mouse button
    if( event.button === 0 && event.ctrlKey ) {
      const pointer = pointerPosition();
      const mapped = selectionRect(pointer, pointer,
        showMagnifier && !magnifier_mouse_follow ? magnifier_rect : null);
      const { x, y } = sourcePoint(mapped);
      setSelectedItem( null );

      // check if mouse if over field highlight
      for(let index = (field?.Entries?.length || 0)-1; index >= 0; index--) {
        const entry = field.Entries[index];
        if (Number(entry.PageNr) !== Number(image_data.page_number)) continue;
        const entry_rect = getHighlightRect(entry);
        const is_inside_x = x > entry_rect.x-buffer_x && x < entry_rect.x + entry_rect.width+buffer_x;
        const is_inside_y = y > entry_rect.y-buffer_y && y < entry_rect.y + entry_rect.height+buffer_y;
        if(is_inside_x && is_inside_y){
          if( selected_item === index ) {
            setSelectedItem( null );
          } else {
            setSelectedItem( index );
          }
          break;
        }
      }
    }
    else if( event.button === 0 ) {
      let is_over_magnifier = false;
      if( magnifier_rect ) {
        const { x: mouse_x, y: mouse_y } = pointerPosition();
        if(  mouse_x > magnifier_rect.x
          && mouse_x < magnifier_rect.x + (magnifier_rect.width)
          && mouse_y > magnifier_rect.y
          && mouse_y < magnifier_rect.y + (magnifier_rect.height) ) {
            is_over_magnifier = true;
        }
      }
      
      if( is_magnifier_held && showMagnifier && !is_over_magnifier ) {
        showMagnifier = false;
      } else {
        showMagnifier = true;
      }
      magnifier_mouse_follow = true;
      drawMagnifier();
    }
    // Right mouse button
    else if( event.button === 2 && event.ctrlKey ) {
      if(selected_item !== null ) {
        removeEntry( field.Id, selected_item );
      }
    }
    else if (event.button === 2){
      highlightCoordsStart();
    }
  }

  //-----------------------------------------------------------------
  function onMouseUp(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    // Left mouse button
    if (event.button === 0) {
      if( is_magnifier_held ) {
        magnifier_mouse_follow = false;
      } else {
        showMagnifier = false;
      }
      drawMagnifier();
    } 
    // Right mouse button
    else if (event.button === 2) {
      highlightCoordsEnd();
      saveHighlightCoords();
    }

    returnFieldFocus();
    // sendMessageData( 'FIELD-FOCUS-REQUEST' );
    window.opener?.focus();
  }

  //-----------------------------------------------------------------
  function onMouseLeave(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    highlightCoordsEnd();
    saveHighlightCoords();
    if( !is_magnifier_held ) {
      showMagnifier = false;
      drawMagnifier();
    }
    magnifier_mouse_follow = false
  }

  //-----------------------------------------------------------------
  function onMouseMove(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    // const skewed = skew(x,y);

    highlightCoordsEnd();
    drawCurrentHighlight();
    if(showMagnifier) drawMagnifier();
  }

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if(showMagnifier) {
      const coord_count = field.Entries[0].Bottom < 0 ? 0 : field.Entries.length;
      const is_coord_allowed = coord_count === 0 || field.validationInfo.AllowMultipleCoords;
      if(event.ctrlKey && is_coord_allowed) highlightCoordsStart();
    }

    if(event.key === 'Delete' && selected_item !== null ) {
      removeEntry( field.Id, selected_item );
    }
  }

  //-----------------------------------------------------------------
  function onContextMenu(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
    return false;
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function drawMagnifier() {
    //-----------------------------------------------------------------
    if( !context_magnify ) return;
    if(!showMagnifier || settings.magnifier_size <= 0) {
      context_magnify.clearRect(0, 0, context_magnify.canvas.width, context_magnify.canvas.height);
      return;
    }

    if( magnifier_mouse_follow ) {
      const width = ((400*settings.magnifier_size)/100)||1;
      const height = ((200*settings.magnifier_size)/100)||1;
      const { x: xx, y: yy } = pointerPosition();
  
      var buffer = document.createElement('canvas');
      buffer.width  = width ;
      buffer.height = height;
  
      buffer.getContext('2d').drawImage(context_base.canvas,xx-width*0.5, yy-height*0.5, width, height, 0,0, width, height );
  
      context_magnify.clearRect(0, 0, context_magnify.canvas.width, context_magnify.canvas.height);
      context_magnify.beginPath();
  
      magnifier_rect = { x: xx-width, y: yy-height, width: width*2, height: height*2 }
      context_magnify.drawImage(buffer,xx-width,yy-height,width*2, height*2);
      context_magnify.rect(xx-width,yy-height,width*2, height*2);
      context_magnify.stroke();
      context_magnify.closePath();
    } else {
      const width  = ((400*settings.magnifier_size)/100)||1;
      const height = ((200*settings.magnifier_size)/100)||1;
      var buffer = document.createElement('canvas');
      buffer.width  = width ;
      buffer.height = height;
  
      buffer.getContext('2d').drawImage(context_base.canvas,magnifier_rect.x+(width*0.5), magnifier_rect.y+(height*0.5), width, height, 0,0, width, height );
  
      context_magnify.clearRect(0, 0, context_magnify.canvas.width, context_magnify.canvas.height);
      context_magnify.beginPath();
  
      context_magnify.drawImage(buffer, magnifier_rect.x, magnifier_rect.y, width*2, height*2);
      context_magnify.rect(magnifier_rect.x, magnifier_rect.y, width*2, height*2);
      context_magnify.stroke();
      context_magnify.closePath();
    }
  }

  //-----------------------------------------------------------------
  function drawCurrentHighlight() {
    //-----------------------------------------------------------------
    if (!drawHighlight || !endCoord || !startCoord) return;
    drawBaseCanvas();
    const rect = currentSelection();
    context_base.save();
    context_base.globalCompositeOperation = 'darken';
    context_base.fillStyle = 'rgb(255,255,0)';
    context_base.fillRect(rect.x, rect.y, rect.width, rect.height);
    context_base.restore();
  }

  //-----------------------------------------------------------------
  function DisplayDirection({direction}) {
    //---------------------------------------------------------------
    if(!direction) return null;

    const isUp = direction === 'up';
    const image_height = canvas_height || image?.naturalHeight;
    const spacing = 200;
    const arrow_number = Math.max(1, Math.round(image_height / spacing));
    const visual = [];
    for  ( let i = 0; i < arrow_number; i++ ) {
      visual.push(i);
    }

    return (
      <div className={`arrow-visual ${isUp ? 'up' : 'down'}`}>
        {visual.map((x) => (
          <div key={x} className={isUp ? 'arrow-item' : 'arrow-item-reverse' } style={{ top: `${(image_height / arrow_number ) * x}px`, animationDelay: `${(isUp ? (arrow_number-x) : x )* 150}ms` }} >
            {isUp ? '⬆' : '⬇'}
          </div>
        ))}
      </div>
    );
  }

  //-----------------------------------------------------------------
  function display() {
    //---------------------------------------------------------------
    
    if(!image_data || !image) return (null);
    else {
      const dimensions = rotatedDimensions( image.naturalWidth, image.naturalHeight, rotation );
      return (
        <>
          <canvas
            name='current_image_canvas'
            style={_styles.canvas_main}
            ref={setBaseRefNode}
            height={dimensions.height}
            width={dimensions.width}
            tabIndex={999}
            onMouseUp={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu}
          />
          <canvas
            name='current_image_canvas'
            style={_styles.canvas_misc}
            ref={setMagnifyRefNode}
            height={dimensions.height}
            width={dimensions.width}
            tabIndex={999}
            onMouseUp={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu}
          />
          <DisplayDirection direction={arrowDirection} />
        </>
      );
    }
  }

  //#endregion

  return display();
};
